import api from './api';

export const b2bService = {
  // ----------------------------------------------------
  // 1. Governance & Party Contracts (REQ-B2B-01, 02)
  // ----------------------------------------------------
  getEligibleParties: async (centreId = null) => {
    const params = centreId ? { centreId } : {};
    const response = await api.get('/b2b/parties/eligible', { params });
    return response.data || [];
  },

  validateEligibility: async (partyId, bookingDate = null) => {
    const params = bookingDate ? { bookingDate } : {};
    const response = await api.get(`/b2b/parties/${partyId}/eligibility`, { params });
    return response.data;
  },

  getPartyContract: async (partyId, transactionDate = null) => {
    const params = transactionDate ? { transactionDate } : {};
    const response = await api.get(`/b2b/parties/${partyId}/contract`, { params });
    return response.data;
  },

  updatePartyLifecycle: async (partyId, data) => {
    const response = await api.put(`/b2b/parties/${partyId}/lifecycle`, data);
    return response.data;
  },

  // ----------------------------------------------------
  // 2. Billing & Credit Policy (REQ-B2B-03, 04, 05)
  // ----------------------------------------------------
  evaluateCredit: async (partyId, orderAmount, allowReservation = false) => {
    const response = await api.post(`/b2b/billing/credit/evaluate?partyId=${partyId}&amount=${orderAmount}&allowReservation=${allowReservation}`);
    return response.data;
  },

  createCreditOverride: async (data) => {
    const response = await api.post('/b2b/billing/credit/override', data);
    return response.data;
  },

  getUnbilledOrders: async (partyId, fromDate, toDate) => {
    const response = await api.get('/b2b/billing/unbilled', {
      params: { partyId, fromDate, toDate }
    });
    return response.data || [];
  },

  generateBatchInvoice: async (data) => {
    const response = await api.post('/b2b/billing/generate-invoice', data);
    return response.data;
  },

  getInvoices: async (params = {}) => {
    const response = await api.get('/b2b/billing/invoices', { params });
    return response.data?.data || [];
  },

  getInvoiceDetails: async (invoiceId) => {
    const response = await api.get(`/b2b/billing/invoices/${invoiceId}`);
    return response.data?.data;
  },

  createAdvance: async (data) => {
    const response = await api.post('/b2b/billing/advances', data);
    return response.data;
  },

  allocatePayment: async (data) => {
    const response = await api.post('/b2b/billing/payments/allocate', data);
    return response.data;
  },

  getAgingReport: async (params = {}) => {
    const response = await api.get('/b2b/billing/aging-report', { params });
    return response.data?.data || [];
  },

  // Pre-Billing AI Anomaly Detection (REQ-B2B-13 - Advisory)
  getBillingAnomalies: async (partyId, fromDate, toDate) => {
    const response = await api.get(`/b2b/billing/anomalies/${partyId}`, {
      params: { fromDate, toDate }
    });
    return response.data?.data || [];
  },

  // ----------------------------------------------------
  // 3. Specimen Logistics (REQ-B2B-08)
  // ----------------------------------------------------
  getManifests: async (params = {}) => {
    const response = await api.get('/b2b/logistics/manifests', { params });
    return response.data?.data || [];
  },

  getManifestDetails: async (manifestId) => {
    const response = await api.get(`/b2b/logistics/manifests/${manifestId}`);
    return response.data?.data;
  },

  createManifest: async (data) => {
    const response = await api.post('/b2b/logistics/manifests', data);
    return response.data?.data;
  },

  addManifestItem: async (manifestId, barcodeNo) => {
    const response = await api.post(`/b2b/logistics/manifests/${manifestId}/items`, { barcodeNo });
    return response.data?.data;
  },

  dispatchManifest: async (manifestId, data) => {
    const response = await api.post(`/b2b/logistics/manifests/${manifestId}/dispatch`, data);
    return response.data;
  },

  receiveManifest: async (manifestId, data) => {
    const response = await api.post(`/b2b/logistics/manifests/${manifestId}/receive`, data);
    return response.data;
  },

  verifyManifestItem: async (data) => {
    const response = await api.post('/b2b/logistics/manifests/items/verify', data);
    return response.data;
  },

  // ----------------------------------------------------
  // 4. External Code Mappings (REQ-B2B-07)
  // ----------------------------------------------------
  getCodeMappings: async (partyId) => {
    const response = await api.get(`/b2b/code-mappings/${partyId}`);
    return response.data?.data || [];
  },

  saveCodeMapping: async (data) => {
    const response = await api.post('/b2b/code-mappings', data);
    return response.data;
  },

  // ----------------------------------------------------
  // 5. B2B Customer Portal (REQ-B2B-09, 10, 11)
  // ----------------------------------------------------
  portalLogin: async (username, password) => {
    const response = await api.post('/b2b/portal/login', { username, password });
    return response.data;
  },

  portalLogout: async () => {
    try {
      await api.post('/b2b/portal/logout');
    } finally {
      sessionStorage.removeItem('b2b_portal_token');
      sessionStorage.removeItem('b2b_portal_user');
    }
  },

  getPortalSession: async () => {
    const response = await api.get('/b2b/portal/session');
    return response.data;
  },

  getPortalDashboard: async () => {
    const response = await api.get('/b2b/portal/dashboard');
    return response.data?.data ?? response.data;
  },

  getPortalOrders: async (params = {}) => {
    const response = await api.get('/b2b/portal/orders', { params });
    return response.data?.data ?? response.data ?? [];
  },

  getPortalReportDetails: async (ledgerTransactionNo) => {
    const response = await api.get(`/b2b/portal/reports/${ledgerTransactionNo}`);
    return response.data?.data ?? response.data;
  },

  getPortalInvoices: async () => {
    const response = await api.get('/b2b/portal/invoices');
    return response.data?.data ?? response.data ?? [];
  },

  getPortalLedgerStatement: async (params = {}) => {
    const response = await api.get('/b2b/portal/ledger-statement', { params });
    return response.data?.data ?? response.data ?? [];
  },

  getBrandingTemplate: async (partyId) => {
    const response = await api.get(`/b2b/portal/branding/${partyId}`);
    return response.data?.data;
  },

  saveBrandingTemplate: async (data) => {
    const response = await api.post('/b2b/portal/branding', data);
    return response.data;
  }
};

export default b2bService;
